import { Component, Input, inject } from '@angular/core'
import { switchMap } from 'rxjs'
import { CommonModule } from '@angular/common'
import { ProfileStore } from '@core/state/profile.store'
import { FormatNamePipe } from '@shared/pipes/format-name-.pipe'
import { AvatarPickerService } from '@shared/services/avatar-picker.service'
import { UserService } from '@core/services/user.service'
import { ToastService } from '@shared/services/toast.service'

export interface LocalUserProfileVM {
  name: string
  role: string
  location: string
  avatar: string
  profession: string
}

@Component({
  selector: 'app-profile-card',
  standalone: true,
  imports: [CommonModule, FormatNamePipe],
  templateUrl: './profile-card.component.html',
  styleUrls: ['./profile-card.component.scss'],
})
export class ProfileCardComponent {
  /**
   * Optional external user input. If not provided, the component will use the global ProfileStore.
   */
  @Input() user?: LocalUserProfileVM

  /** Inject profile store to source current user data when no input is provided */
  protected readonly profileStore = inject(ProfileStore)
  private readonly avatarPickerService = inject(AvatarPickerService)
  private readonly userService = inject(UserService)
  private readonly toastService = inject(ToastService)

  /**
   * Build a view model from store, used when no @Input user is passed.
   */
  get vmFromStore(): LocalUserProfileVM | null {
    const p = this.profileStore.profile()
    if (!p) return null

    // Build location by filtering out null/undefined values
    const locationParts: string[] = []
    if (p.city) locationParts.push(p.city)
    if (p.country) locationParts.push(p.country)
    const location = locationParts.join(', ').trim()

    return {
      name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || '',
      role: p.role ? String(p.role) : '',
      profession: p.profession || '',
      location: location || '',
      avatar: p.profilePhoto || 'assets/images/Fille-brune.png',
    }
  }

  /**
   * Get the current user profile from input or store
   */
  get currentUser(): LocalUserProfileVM | null {
    return this.user || this.vmFromStore
  }

  /**
   * Check if a value is valid for display (not null, undefined, empty, or 'null' string)
   */
  openAvatarPicker(): void {
    this.avatarPickerService.open({
      currentAvatar: this.profileStore.avatar(),
      onSelected: url => this.onAvatarSelected(url),
      onFileSelected: (file, previewUrl) => this.onFileUploaded(file, previewUrl),
    })
  }

  private onAvatarSelected(url: string): void {
    const userId = this.profileStore.profile()?.id
    if (!userId) return

    this.userService.updateAvatar(userId, url).subscribe({
      next: () => {
        this.profileStore.setAvatar(url)
        this.toastService.show('success', '', 'Photo de profil mise à jour.')
      },
      error: () => {
        this.toastService.show('error', '', 'Impossible de mettre à jour la photo de profil.')
      },
    })
  }

  private onFileUploaded(file: File, previewUrl: string): void {
    const userId = this.profileStore.profile()?.id
    if (!userId) return

    // Optimistic update with local preview while uploading
    this.profileStore.setAvatar(previewUrl)

    this.userService
      .uploadAvatar(userId, file)
      .pipe(switchMap(() => this.userService.getAvatar(userId)))
      .subscribe({
        next: avatarUrl => {
          if (avatarUrl) this.profileStore.setAvatar(avatarUrl)
          this.toastService.show('success', '', 'Photo de profil mise à jour.')
        },
        error: () => {
          // Rollback optimistic update
          this.profileStore.setAvatar('')
          this.toastService.show('error', '', "Impossible d'uploader la photo de profil.")
        },
      })
  }

  isValid(value: string | null | undefined): boolean {
    if (value == null || value === '' || value === 'null') {
      return false
    }
    return value.trim() !== ''
  }
}
